import { memo } from "react";
import "./QuotationLuggage.scss";
import PropTypes from "prop-types";
import { luggageShape } from "app/utils/propTypes";
import { BAGGAGE_TYPES, FLIGHT_OPTIONS_STATUS, TRANSPORTATION_TYPES } from "app/constants";
import AlertMessage from "app/components/ui/AlertMessage/AlertMessage";
import { FormattedMessage, FormattedPlural } from "react-intl";
import QuotationLuggageItem from "app/pages/Booking/Quotation/Luggage/QuotationLuggageItem";
import LuggagePlaceholderLoader from "./LuggagePlaceholderLoader/LuggagePlaceholderLoader";
import IconHandLuggageAvailable from "app/pages/.shared/IconHandLuggageAvailable";
import { getBaggageCount, getCabinBaggageCount } from "../luggageHelpers";
import {
	BaggageMFilledIcon,
	BaggageLFilledIcon,
	BaggageMDisabledFilledIcon,
	BaggageLDisabledFilledIcon,
} from "app/icons/travel";

const QuotationLuggage = ({
	informativeOnly,
	hasInfant,
	flightOptionsStatus,
	luggageMaxCount,
	flightBagageOption = {},
	selectedCheckedBaggageOption = {},
	selectedCabinBaggage = {},
	flightBagageOptions = [],
	onLuggageChange,
	onCabinLuggageChange,
	selectedTransportType,
	displayFlightPriceInfoBubble = false,
}) => {
	const { cabinBaggage } = flightBagageOption;
	const holdBaggageCount = getBaggageCount(
		flightBagageOption,
		selectedCheckedBaggageOption?.quantity,
		luggageMaxCount
	);
	const cabinBaggageCount = getCabinBaggageCount(
		cabinBaggage,
		selectedCabinBaggage?.quantity,
		luggageMaxCount
	);

	const isSelectedTransportTrain = selectedTransportType === TRANSPORTATION_TYPES.TRAIN;

	const isLoading = flightOptionsStatus === FLIGHT_OPTIONS_STATUS.LOADING;

	if (isLoading) {
		return <LuggagePlaceholderLoader />;
	}

	const handLuggageMessage = flightBagageOption?.handBaggage?.description;
	const cabinLuggageMessage = flightBagageOption?.cabinBaggage?.description;
	// details contient par exemple les dimentsions des bagages ou le poids max autorisé
	// ex: "55 x 40 x 20 cm"
	// ou "20 kg max / bagage"
	const handLuggageDetails = flightBagageOption?.handBaggage?.details;
	const cabinLuggageDetails = flightBagageOption?.cabinBaggage?.details;
	const allLuggageOptionsHaveSameMaxWeight =
		flightBagageOptions?.length > 0 &&
		flightBagageOptions[0]?.maxWeightPerBaggage > 0 &&
		flightBagageOptions?.every(
			option =>
				option?.maxWeightPerBaggage === flightBagageOptions[0]?.maxWeightPerBaggage &&
				Boolean(option?.maxWeightPerBaggage)
		);

	const holdLuggageIncluded = flightBagageOption?.included;

	let holdLuggageDetails = null;
	if (holdLuggageIncluded && flightBagageOption?.maxWeightPerBaggage > 0) {
		holdLuggageDetails = (
			<FormattedMessage
				id="quotation.transport.luggage.option.max.weight"
				values={{
					weight: flightBagageOption?.maxWeightPerBaggage,
				}}
			/>
		);
	} else if (
		!allLuggageOptionsHaveSameMaxWeight &&
		selectedCheckedBaggageOption?.maxWeightPerBaggage > 0
	) {
		// affichage des poids des bagages selectionnées par le user
		holdLuggageDetails = (
			<FormattedMessage
				id="quotation.transport.luggage.option.max.weight"
				values={{
					weight: selectedCheckedBaggageOption?.maxWeightPerBaggage,
				}}
			/>
		);
	} else if (allLuggageOptionsHaveSameMaxWeight) {
		holdLuggageDetails = (
			<FormattedMessage
				id="quotation.transport.luggage.option.max.weight"
				values={{
					weight: flightBagageOptions[0]?.maxWeightPerBaggage,
				}}
			/>
		);
	}

	const handLuggageIncluded = flightBagageOption?.handBaggage?.included;

	return (
		<div className="quotation-luggage">
			{hasInfant && (
				<div className="quotation-luggage__message">
					<AlertMessage
						message={<FormattedMessage id="quotation.luggage.baby.warning.message" />}
					/>
				</div>
			)}
			<div className="quotation-luggages">
				{/* BAGAGE A MAIN */}

				{handLuggageIncluded && (
					<div className="quotation-luggages__item" data-testid="hand-luggage">
						<QuotationLuggageItem
							informativeOnly={informativeOnly}
							picto={<IconHandLuggageAvailable />}
							baggageCount={luggageMaxCount}
							isIncluded={handLuggageIncluded}
							description={handLuggageMessage}
							details={handLuggageDetails}
							isSelectedTransportTrain={isSelectedTransportTrain}
							luggageType={BAGGAGE_TYPES.HAND_BAGGAGE}
							label={
								<FormattedPlural
									value={luggageMaxCount}
									one={
										<FormattedMessage
											id="quotation.luggage.handbag.singular.label"
											values={{
												luggageMaxCount,
											}}
										/>
									}
									other={
										<FormattedMessage
											id="quotation.luggage.handbag.plural.label"
											values={{
												luggageMaxCount,
											}}
										/>
									}
								/>
							}
						/>
					</div>
				)}

				{/* BAGAGE EN CABINE */}
				<div className="quotation-luggages__item" data-testid="cabin-luggage">
					<QuotationLuggageItem
						informativeOnly={informativeOnly}
						picto={
							cabinBaggageCount <= 0 ? (
								<BaggageMDisabledFilledIcon />
							) : (
								<BaggageMFilledIcon />
							)
						}
						baggageCount={cabinBaggageCount}
						isIncluded={cabinBaggage?.included === true}
						description={cabinLuggageMessage}
						details={cabinLuggageDetails}
						canBeBooked={cabinBaggage?.canBeBooked === true}
						flightBagageOptions={flightBagageOption.cabinBaggages}
						onLuggageChange={onCabinLuggageChange}
						totalUpgradePrice={selectedCabinBaggage?.pricePerBooking}
						publicUpgradePrice={selectedCabinBaggage?.publicPricePerBooking}
						luggageType={BAGGAGE_TYPES.CABIN_BAGGAGE}
						label={
							<FormattedPlural
								value={cabinBaggageCount}
								one={
									isSelectedTransportTrain ? (
										<FormattedMessage id="quotation.transport.luggage.small" />
									) : (
										<FormattedMessage id="quotation.luggage.cabin.singular.label" />
									)
								}
								other={
									isSelectedTransportTrain ? (
										<FormattedMessage id="quotation.transport.luggage.small.plural" />
									) : (
										<FormattedMessage id="quotation.luggage.cabin.plural.label" />
									)
								}
							/>
						}
					/>
				</div>

				{/* BAGAGE EN SOUTE */}
				<div className="quotation-luggages__item" data-testid="luggage">
					<QuotationLuggageItem
						informativeOnly={informativeOnly}
						displayFlightPriceInfoBubble={displayFlightPriceInfoBubble}
						isIncluded={flightBagageOption.included}
						canBeBooked={flightBagageOption.canBeBooked}
						baggageCount={holdBaggageCount}
						description={flightBagageOption.description}
						totalUpgradePrice={selectedCheckedBaggageOption?.pricePerBooking}
						publicUpgradePrice={selectedCheckedBaggageOption?.publicPricePerBooking}
						flightBagageOptions={flightBagageOptions}
						picto={
							flightBagageOption.included || holdBaggageCount > 0 ? (
								<BaggageLFilledIcon />
							) : (
								<BaggageLDisabledFilledIcon />
							)
						}
						details={holdLuggageDetails}
						onLuggageChange={onLuggageChange}
						luggageType={
							isSelectedTransportTrain
								? BAGGAGE_TYPES.TRAIN_BAGGAGE
								: BAGGAGE_TYPES.CHECKED_BAGGAGE
						}
						label={
							<FormattedPlural
								value={holdBaggageCount}
								one={
									isSelectedTransportTrain ? (
										<FormattedMessage id="quotation.transport.luggage.regular" />
									) : (
										<FormattedMessage id="quotation.luggage.singular.label" />
									)
								}
								other={
									isSelectedTransportTrain ? (
										<FormattedMessage id="quotation.transport.luggage.regular.pluralized" />
									) : (
										<FormattedMessage id="quotation.luggage.plural.label" />
									)
								}
							/>
						}
					/>
				</div>
			</div>
		</div>
	);
};

QuotationLuggage.propTypes = {
	flightBagageOptions: PropTypes.array,
	informativeOnly: PropTypes.bool,
	hasInfant: PropTypes.bool,
	onLuggageChange: PropTypes.func,
	displayFlightPriceInfoBubble: PropTypes.bool,
	onCabinLuggageChange: PropTypes.func,
	luggageMaxCount: PropTypes.number,
	selectedCheckedBaggageOption: PropTypes.shape({
		quantity: PropTypes.number,
		maxWeightPerBaggage: PropTypes.number,
	}),
	selectedCabinBaggage: PropTypes.shape({
		quantity: PropTypes.number,
		maxWeightPerBaggage: PropTypes.number,
	}),
	flightBagageOption: luggageShape,
	selectedTransportType: PropTypes.string,
	flightOptionsStatus: PropTypes.oneOf([
		FLIGHT_OPTIONS_STATUS.LOADING,
		FLIGHT_OPTIONS_STATUS.NO_BAGAGE,
		FLIGHT_OPTIONS_STATUS.SUCCESS,
	]),
};

export default memo(QuotationLuggage);
